import { useState, useEffect } from 'react';
import { client } from '../../sdkAdapter';
import Swal from 'sweetalert2';
import {
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableRow,
} from "../ui/table";
import { Modal } from "../ui/modal";
import Badge from "../ui/badge/Badge";
import MediaUploader from '../common/MediaUploader';
import MediaPickerModal from '../common/MediaPickerModal';

export default function CMSBanners() {
    const [banners, setBanners] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form, setForm] = useState({
        type: 'Main Slider',
        title: '',
        subtitle: '',
        short_description: '',
        url: '',
        status: true,
        image_id: null as number | null,
        image_preview: null as string | null
    });
    const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            const res = (client.getFrontBanners ? await client.getFrontBanners() : await client.getV1Banners()) as any;
            const data = res.data?.data?.data || res.data?.data || res.data || [];
            setBanners(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Failed to fetch banners:', err);
            setBanners([]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.title || !form.type) {
            Swal.fire({
                title: 'Validation Error',
                text: 'Title and Type are required',
                icon: 'warning'
            });
            return;
        }
        setLoading(true);
        try {
            if (editingId) {
                await (client as any).postV1BannerUpdate({
                    postV1BannerUpdateRequest: {
                        id: editingId,
                        type: form.type,
                        title: form.title,
                        subtitle: form.subtitle,
                        short_description: form.short_description,
                        url: form.url,
                        status: form.status,
                        images: form.image_id ? [form.image_id] : undefined
                    }
                });
            } else {
                await client.postV1BannerCreate({
                    postV1BannerCreateRequest: {
                        type: form.type,
                        title: form.title,
                        subtitle: form.subtitle,
                        short_description: form.short_description,
                        url: form.url,
                        status: form.status,
                        images: form.image_id ? [form.image_id] : undefined
                    }
                } as any);
            }
            setIsModalOpen(false);
            setForm({ type: 'Main Slider', title: '', subtitle: '', short_description: '', url: '', status: true, image_id: null, image_preview: null });
            setEditingId(null);
            fetchBanners();

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: editingId ? 'Banner updated successfully' : 'Banner created successfully',
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true
            });
        } catch (err: any) {
            console.error(err);
            Swal.fire({
                title: 'Error',
                text: err?.response?.data?.message || err?.message || 'Failed to save banner',
                icon: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingId(null);
        setForm({ type: 'Main Slider', title: '', subtitle: '', short_description: '', url: '', status: true, image_id: null, image_preview: null });
        setIsModalOpen(true);
    };

    const handleEdit = (banner: any) => {
        setEditingId(banner.id);
        const firstImage = banner.images && banner.images.length > 0 ? banner.images[0] : null;
        setForm({
            type: banner.type || 'Main Slider',
            title: banner.title || '',
            subtitle: banner.subtitle || '',
            short_description: banner.short_description || '',
            url: banner.url || '',
            status: !!banner.status,
            image_id: firstImage ? (typeof firstImage === 'object' ? firstImage.id : firstImage) : null,
            image_preview: firstImage && typeof firstImage === 'object' ? firstImage.path : null
        });
        setIsModalOpen(true);
    };

    const handleDelete = async (id: number) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (!result.isConfirmed) return;
        try {
            await client.postV1BannerDelete({
                postV1BannerDeleteRequest: { id: id }
            } as any);
            fetchBanners();
        } catch (err) {
            console.error('Failed to delete banner:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to delete banner',
                icon: 'error'
            });
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Banners</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors text-sm"
                    >
                        Create Banner
                    </button>
                </div>
            </div>
            <div className="overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableCell isHeader className="px-5 py-3 text-start">Image</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Title</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Type</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-start">Status</TableCell>
                            <TableCell isHeader className="px-5 py-3 text-end">Actions</TableCell>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {banners.length === 0 ? (
                            <TableRow><TableCell colSpan={5} className="px-5 py-4 text-center">No banners found.</TableCell></TableRow>
                        ) : (
                            banners.map(banner => (
                                <TableRow key={banner.id}>
                                    <TableCell className="px-5 py-4">
                                        {banner.images && banner.images.length > 0 && typeof banner.images[0] === 'object' && banner.images[0].path ? (
                                            <img
                                                src={banner.images[0].path.startsWith('http')
                                                    ? banner.images[0].path
                                                    : `${import.meta.env.VITE_API_BASE || 'http://localhost:8000'}/${banner.images[0].path.replace(/^\//, '')}`}
                                                alt=""
                                                className="w-12 h-12 object-cover rounded-md shadow-sm border border-gray-100 dark:border-gray-800"
                                            />
                                        ) : (
                                            <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center text-xs text-gray-400">No Image</div>
                                        )}
                                    </TableCell>
                                    <TableCell className="px-5 py-4 font-medium text-gray-900 dark:text-white">{banner.title}</TableCell>
                                    <TableCell className="px-5 py-4 text-sm text-gray-500">{banner.type}</TableCell>
                                    <TableCell className="px-5 py-4">
                                        <Badge color={banner.status ? 'success' : 'error'} size="sm">
                                            {banner.status ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="px-4 py-3 text-end">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(banner)}
                                                className="text-blue-600 hover:underline"
                                                title="Edit Banner"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(banner.id)}
                                                className="text-red-600 hover:underline"
                                                title="Delete Banner"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} className="max-w-xl" showCloseButton={false}>
                <div className="flex flex-col max-h-[90vh] bg-white dark:bg-gray-900 rounded-3xl overflow-hidden shadow-2xl">
                    {/* Fixed Header */}
                    <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                        <h3 className="text-xl font-bold text-gray-800 dark:text-white">
                            {editingId ? 'Edit Banner' : 'Create Banner'}
                        </h3>
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        <form id="cms-banner-form" onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={form.type}
                                        onChange={e => setForm({ ...form, type: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                                    >
                                        <option value="Main Slider">Main Slider</option>
                                        <option value="First Group of banners">First Group of banners</option>
                                        <option value="Promotion banner">Promotion banner</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={form.title}
                                        onChange={e => setForm({ ...form, title: e.target.value })}
                                        placeholder="Enter banner title"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                        required
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">
                                        Subtitle <span className="text-red-500">*</span></label>
                                    <input
                                        type="text"
                                        value={form.subtitle}
                                        required
                                        onChange={e => setForm({ ...form, subtitle: e.target.value })}
                                        placeholder="Enter subtitle"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">URL</label>
                                    <input
                                        type="text"
                                        value={form.url}
                                        onChange={e => setForm({ ...form, url: e.target.value })}
                                        placeholder="Banner link URL"
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Short Description</label>
                                <textarea
                                    value={form.short_description}
                                    onChange={e => setForm({ ...form, short_description: e.target.value })}
                                    placeholder="Enter description..."
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm min-h-[100px]"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                                <div className="space-y-4">
                                    <MediaUploader
                                        label="Banner Image"
                                        initialImages={form.image_id ? [form.image_id] : []}
                                        onUpload={(ids) => {
                                            setForm({ ...form, image_id: ids[0] || null });
                                        }}
                                        multiple={false}
                                        folder="banners"
                                    />
                                    {form.image_id && (
                                        <div className="mt-2 p-2 border border-gray-100 dark:border-gray-800 rounded-xl bg-gray-50/50 dark:bg-gray-800/50">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 px-1">Current Selection</p>
                                            <div className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 shadow-sm">
                                                {form.image_preview ? (
                                                    <img
                                                        src={form.image_preview.startsWith('http')
                                                            ? form.image_preview
                                                            : `${import.meta.env.VITE_API_BASE || 'http://localhost:8000'}/${form.image_preview.replace(/^\//, '')}`}
                                                        className="w-full h-full object-cover"
                                                        alt="Preview"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-gray-900 text-[10px] text-gray-400">ID: {form.image_id}</div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-between items-center bg-gray-50 dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">Select from Media Library</span>
                                        <button
                                            type="button"
                                            onClick={() => setIsMediaModalOpen(true)}
                                            className="text-sm text-brand-500 hover:text-brand-600 font-bold transition-colors"
                                        >
                                            Open Library
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                                    <select
                                        value={form.status ? 'true' : 'false'}
                                        onChange={e => setForm({ ...form, status: e.target.value === 'true' })}
                                        className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-white focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 transition-all outline-none text-sm appearance-none cursor-pointer"
                                    >
                                        <option value="true">Active</option>
                                        <option value="false">Inactive</option>
                                    </select>
                                </div>
                            </div>
                        </form>
                    </div>

                    {/* Fixed Footer */}
                    <div className="px-6 py-5 border-t border-gray-100 dark:border-gray-800 flex justify-end gap-3 bg-gray-50/50 dark:bg-gray-800/30">
                        <button
                            type="button"
                            onClick={() => setIsModalOpen(false)}
                            className="px-6 py-2.5 text-sm font-bold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            form="cms-banner-form"
                            className="px-6 py-2.5 text-sm font-bold text-white bg-brand-500 border border-brand-500 rounded-xl hover:bg-brand-600 shadow-[0_4px_12px_rgba(59,130,246,0.3)] transition-all active:scale-95"
                        >
                            {editingId ? 'Update Banner' : 'Create Banner'}
                        </button>
                    </div>
                </div>
            </Modal>

            <MediaPickerModal
                isOpen={isMediaModalOpen}
                onClose={() => setIsMediaModalOpen(false)}
                onSelect={(file) => {
                    setForm({ ...form, image_id: file.id, image_preview: file.path || null });
                    setIsMediaModalOpen(false);
                }}
            />
        </div>
    );
}
